import { createInput, createSelect, MountTarget, newDialog, readConfig } from './utils/elements';

export const PIJLENSCHEMA_EDIT_SELECTOR = '.cds-lrn-pijlenschema,.cds-lrn-fractions-multiply,.cds-lrn-fractions-number,.cds-lrn-time-difference';

interface PijlenschemaConfig {
  type: string;
  fwdArrow?: boolean;
  bwdArrow?: boolean;
  withLabels?: boolean;
  amount?: number;
}

export function pijlenschemaDialog(
  insertHtml: (html: string) => void,
  mountTarget?: MountTarget,
  existingEl?: HTMLElement
) {
  const dialog = newDialog('Pijlenschema maken', () => insertHtml(''), mountTarget);

  const arrowType = createSelect('type', 'Type', [
    { label: 'Stappen', value: 'steps' },
    { label: 'Breuk van een getal', value: 'fractions-number' },
    { label: 'Breuken vermenigvuldigen', value: 'fractions-multiply' },
    { label: 'Tijdsverschil', value: 'time-difference' },
  ]);

  const contentDiv = document.createElement('div');

  const fwdArrow = createInput('fwdArrow', 'Voorwaartse pijl', 'checkbox');
  const bwdArrow = createInput('bwdArrow', 'Achterwaartse pijl', 'checkbox');
  const withLabels = createInput('withLabels', 'Met tekst op de pijlen', 'checkbox');
  const amount = createInput('amount', 'Aantal pijlen', 'number');

  const renderContent = (type: string) => {
    contentDiv.innerHTML = '';
    if (type === 'steps') {
      contentDiv.append(fwdArrow.field, bwdArrow.field, withLabels.field, amount.field);
    }
  };
  arrowType.field.addEventListener('change', () => renderContent(arrowType.value()));

  if (existingEl) {
    const cfg = readConfig<PijlenschemaConfig>(existingEl, { type: 'steps' });
    if (cfg.type) {
      arrowType.setValue(cfg.type);
      renderContent(cfg.type);
      fwdArrow.setChecked(!!cfg.fwdArrow);
      bwdArrow.setChecked(!!cfg.bwdArrow);
      withLabels.setChecked(!!cfg.withLabels);
      if (cfg.amount) amount.setValue(cfg.amount);
    }
  }

  dialog.querySelector('#cds-dialog-content').append(arrowType.field, contentDiv);

  dialog.addEventListener('onSubmit', () => {
    const config: PijlenschemaConfig = { type: arrowType.value() };
    let result: string;
    switch (arrowType.value()) {
      case 'steps':
        config.fwdArrow = fwdArrow.checked();
        config.bwdArrow = bwdArrow.checked();
        config.withLabels = withLabels.checked();
        config.amount = +amount.value();
        result = makePijlenschema(config);
        break;
      case 'fractions-number':
        result = makeFractionsNumber(config);
        break;
      case 'fractions-multiply':
        result = makeFractionsMultiply(config);
        break;
      case 'time-difference':
        result = makeTimeDifference(config);
        break;
    }
    insertHtml(result);
  });
}

function makePijlenschema(config: PijlenschemaConfig) {
  const fwdArrow = !!config.fwdArrow;
  const bwdArrow = !!config.bwdArrow;
  const withLabels = !!config.withLabels;
  let amount = config.amount;
  amount = amount || 1;

  //schema
  const schema = document.createElement('table');
  schema.classList.add('cds-lrn-pijlenschema');
  schema.setAttribute('data-cb-config', JSON.stringify(config));

  const cell = (tabindex: number, valign: string = 'middle') => {
    const cell = document.createElement('td');
    cell.classList.add('p-i-xs', 'p-b-2xs', 'text-center', 'white-space-nowrap');
    cell.style.verticalAlign = valign;
    if (tabindex > -1) {
      cell.setAttribute('data-tabindex', `${tabindex - 1}`);
    }
    return cell;
  };

  const middleRow = document.createElement('tr');
  middleRow.classList.add('h-0');

  const topRow = document.createElement('tr');
  const bottomRow = document.createElement('tr');

  const startCell = cell(1);
  startCell.innerHTML = 'start';

  if (withLabels) {
    startCell.rowSpan = 3;
    topRow.append(startCell);
  } else {
    middleRow.append(startCell);
  }

  for (let i = 0; i < amount; i++) {
    const arrowCell = cell(-1);
    arrowCell.append(makeArrow(fwdArrow, bwdArrow));

    middleRow.append(arrowCell);

    const topCell = cell((i + 1) * 2, 'bottom');
    const bottomCell = cell(1 + 2 * amount + amount - i, 'top');

    if (withLabels) {
      topCell.innerHTML = 'top';
      if (bwdArrow) {
        bottomCell.innerHTML = 'bottom';
      }
    }
    topRow.append(topCell);

    bottomRow.append(bottomCell);

    const endCell = cell((i + 1) * 2 + 1);
    endCell.innerHTML = 'end';
    if (withLabels) {
      endCell.rowSpan = 3;
      topRow.append(endCell);
    } else {
      middleRow.append(endCell);
    }
  }

  if (withLabels) {
    schema.append(topRow);
  }

  schema.append(middleRow);

  if (withLabels) {
    schema.append(bottomRow);
  }

  return schema.outerHTML;
}

function makeArrow(fwdArrow: boolean, bwdArrow: boolean) {
  const container = document.createElement('div');
  container.classList.add('flex', 'flex-column', 'items-center', 'gap-3xs');

  //arrow svg

  const arrowTop = document.createElement('div');
  arrowTop.style.height = '14px';

  const arrowBottom = document.createElement('div');
  arrowBottom.style.height = '14px';

  container.append(arrowTop, arrowBottom);

  if (fwdArrow) {
    const fwdArrow = getSvg().cloneNode(true) as HTMLElement;
    arrowTop.append(fwdArrow);
  }
  if (bwdArrow) {
    const bwdArrow = getSvg('primary').cloneNode(true) as HTMLElement;
    bwdArrow.style.transform = 'rotate(180deg)';
    arrowBottom.append(bwdArrow);
  }

  return container;
}

function makeFractionsMultiply(config: PijlenschemaConfig) {
  const table = document.createElement('table');
  table.classList.add('cds-lrn-fractions-multiply', 'body-large');
  table.setAttribute('data-cb-config', JSON.stringify(config));

  const topMultiplyRow = document.createElement('tr');
  const topArrowRow = document.createElement('tr');
  const centerRow = document.createElement('tr');
  const bottomArrowRow = document.createElement('tr');
  const bottomMultiplyRow = document.createElement('tr');

  const spacerCell = document.createElement('td');
  const arrowCell = document.createElement('td');
  const equalsCell = document.createElement('td');
  const fractionACell = document.createElement('td');
  const fractionBCell = document.createElement('td');
  const multiplyCell = document.createElement('td');

  spacerCell.innerHTML = '&nbsp;';
  arrowCell.append(getSvg());
  equalsCell.innerHTML = '=';
  fractionACell.innerHTML = 'A';
  fractionBCell.innerHTML = 'B';
  multiplyCell.innerHTML = 'x 2';

  arrowCell.colSpan = 3;
  fractionACell.colSpan = 2;
  fractionBCell.colSpan = 2;
  multiplyCell.colSpan = 5;

  equalsCell.classList.add('text-center');
  multiplyCell.classList.add('text-center');

  const bottomArrow = getSvg();
  bottomArrow.style.transform = 'scale(1, -1)';
  const bottomArrowCell = arrowCell.cloneNode() as HTMLElement;
  bottomArrowCell.append(bottomArrow);

  topMultiplyRow.append(multiplyCell.cloneNode(true));
  topArrowRow.append(spacerCell.cloneNode(), arrowCell.cloneNode(true), spacerCell.cloneNode());
  centerRow.append(fractionACell, equalsCell, fractionBCell);
  bottomArrowRow.append(spacerCell.cloneNode(), bottomArrowCell, spacerCell.cloneNode());
  bottomMultiplyRow.append(multiplyCell.cloneNode(true));

  table.append(topMultiplyRow, topArrowRow, centerRow, bottomArrowRow, bottomMultiplyRow);

  return table.outerHTML;
}

function getSvg(color: string = 'stroke', large = false) {
  const svg = document.createElement('svg');
  svg.setAttribute('viewBox', `0 0 ${large ? 80 : 40} 7`);
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.classList.add('block');
  svg.style.height = '14px';
  svg.style.width = large ? '160px' : '80px';
  svg.style.overflow = 'visible';

  const path = document.createElement('path');
  path.setAttribute('d', large ? 'M0,7s38.34-12.49,76.69-1.86' : 'M0,7s18.01-11.5,36.94-1.76');
  path.classList.add(`stroke-${color}`);
  path.style.fill = 'none';
  path.style.strokeLinecap = 'round';
  path.style.strokeMiterlimit = '10';

  const arrow = document.createElement('polygon');
  arrow.setAttribute(
    'points',
    large ? '80 7 74.67 6.33 76.69 5.14 76.66 2.79 80 7' : '40 7 34.67 6.33 36.69 5.14 36.66 2.79 40 7'
  );
  arrow.classList.add(`fill-${color}`);
  arrow.style.strokeWidth = '0px';

  svg.append(path, arrow);

  return svg;
}

function makeFractionsNumber(config: PijlenschemaConfig) {
  return `
  <table class="cds-lrn-fractions-number" data-cb-config='${JSON.stringify(config).replace(/'/g, '&#39;')}'>
    <tbody>
      <tr>
        <td class="text-right" style="vertical-align: middle;"><svg class="m-bs-m" fill="none" height="50" viewbox="0 0 53 64" width="41" xmlns="http://www.w3.org/2000/svg"> <path d="M52.5 11L42.0983 5.98615L42.957 17.5012L52.5 11ZM28.9037 62.5718C24.5948 53.4796 21.5772 45.9762 19.9227 39.7883C18.265 33.5884 18.0062 28.8169 19.0922 25.1319C20.1618 21.5026 22.5745 18.7999 26.5738 16.7878C30.6125 14.756 36.2385 13.4475 43.632 12.6636L43.4211 10.6748C35.9439 11.4675 30.0347 12.8078 25.675 15.0012C21.2759 17.2143 18.4273 20.3132 17.1738 24.5665C15.9367 28.7641 16.2931 33.956 17.9906 40.3049C19.6913 46.6659 22.7687 54.2965 27.0964 63.4283L28.9037 62.5718Z" fill="#857370"></path> <path d="M10.4584 28H7.74813L5.145 23.7661L2.54188 28H0L3.71328 22.2272L0.237344 16.8066H2.85578L5.2675 20.8337L7.63328 16.8066H10.1905L6.67625 22.3573L10.4584 28Z" fill="#857370"></path> </svg></td>
        <td style="vertical-align: middle;">BREUK</td>
        <td style="vertical-align: middle;">van</td>
        <td style="vertical-align: middle;">GETAL</td>
        <td style="vertical-align: middle;">is UITKOMST</td>
      </tr>
      <tr>
        <td class="text-right" style="vertical-align: top;">placeholder</td>
        <td class="text-right" colspan="3" rowspan="1"><svg class="-m-bs-m" fill="none" height="50" viewbox="0 0 108 62" width="87" xmlns="http://www.w3.org/2000/svg"> <path d="M3.59192e-06 35.9998L8.39919 43.9237L11.0618 32.6878L3.59192e-06 35.9998ZM98.0515 0.682953C91.048 21.6457 82.2544 33.6537 68.6752 39.0345C55.0001 44.4533 36.2324 43.2619 8.98249 37.1008L8.54143 39.0516C35.8054 45.2159 55.1166 46.5585 69.412 40.8939C83.8034 35.1912 92.8718 22.4984 99.9485 1.3167L98.0515 0.682953Z" fill="#857370"></path> <path d="M88.8958 44.9052C88.8958 44.4764 89.0106 44.1523 89.2403 43.9328C89.47 43.7133 89.8043 43.6036 90.2433 43.6036C90.6669 43.6036 90.9936 43.7159 91.2233 43.9405C91.4581 44.1651 91.5755 44.4866 91.5755 44.9052C91.5755 45.3084 91.4581 45.6274 91.2233 45.8622C90.9885 46.0919 90.6618 46.2067 90.2433 46.2067C89.8145 46.2067 89.4828 46.0944 89.248 45.8698C89.0132 45.6402 88.8958 45.3186 88.8958 44.9052ZM88.8958 38.5811C88.8958 38.1523 89.0106 37.8282 89.2403 37.6087C89.47 37.3893 89.8043 37.2795 90.2433 37.2795C90.6669 37.2795 90.9936 37.3918 91.2233 37.6164C91.4581 37.841 91.5755 38.1626 91.5755 38.5811C91.5755 38.9945 91.4555 39.3161 91.2156 39.5458C90.9808 39.7704 90.6567 39.8827 90.2433 39.8827C89.8145 39.8827 89.4828 39.7704 89.248 39.5458C89.0132 39.3212 88.8958 38.9996 88.8958 38.5811Z" fill="#857370"></path> </svg></td>
        <td></td>
      </tr>
    </tbody>
  </table>
`;
}

function makeTimeDifference(config: PijlenschemaConfig) {
  const table = document.createElement('table');
  table.classList.add('cds-lrn-time-difference', 'body-large');
  table.setAttribute('data-cb-config', JSON.stringify(config));

  const clockRow = document.createElement('tr');
  const intermediateRow = document.createElement('tr');
  const arrowRow = document.createElement('tr');
  const timeRow = document.createElement('tr');
  const additionArrowRow = document.createElement('tr');
  const additionRow = document.createElement('tr');

  const startTimeCell = document.createElement('td');
  const intermediateTimeCell = document.createElement('td');
  const endTimeCell = document.createElement('td');

  const emptyCell = document.createElement('td');

  const startClockCell = document.createElement('td');
  const endClockCell = document.createElement('td');

  const additionCellA = document.createElement('td');
  const additionCellB = document.createElement('td');

  const arrowCell = document.createElement('td');
  const totalAdditionArrowCell = document.createElement('td');

  const totalAdditionCell = document.createElement('td');

  startClockCell.colSpan = 3;
  endClockCell.colSpan = 2;
  totalAdditionCell.colSpan = 3;
  totalAdditionArrowCell.colSpan = 3;

  startClockCell.classList.add('text-left');
  endClockCell.classList.add('text-right');

  startTimeCell.classList.add('text-center');
  intermediateTimeCell.classList.add('text-center');
  endTimeCell.classList.add('text-center');

  additionCellA.classList.add('text-center');
  additionCellB.classList.add('text-center');
  totalAdditionCell.classList.add('text-center');

  startClockCell.append('12:00');
  endClockCell.append('13:20');

  arrowCell.append(getSvg());
  const totalArrow = getSvg('stroke', true);
  totalArrow.style.transform = 'scale(1, -1)';
  totalArrow.style.width = '100%';
  totalAdditionArrowCell.append(totalArrow);

  startTimeCell.append('12:00');
  intermediateTimeCell.append('13:00');
  endTimeCell.append('13:20');

  additionCellA.append('60 min.');
  additionCellB.append('20 min.');

  totalAdditionCell.append('80 min. of 1 uur en 20 min.');

  clockRow.append(startClockCell, endClockCell);
  intermediateRow.append(
    emptyCell.cloneNode(),
    additionCellA,
    emptyCell.cloneNode(),
    additionCellB,
    emptyCell.cloneNode()
  );
  arrowRow.append(
    emptyCell.cloneNode(),
    arrowCell.cloneNode(true),
    emptyCell.cloneNode(),
    arrowCell.cloneNode(true),
    emptyCell.cloneNode()
  );
  timeRow.append(startTimeCell, emptyCell.cloneNode(), intermediateTimeCell, emptyCell.cloneNode(), endTimeCell);
  additionArrowRow.append(emptyCell.cloneNode(), totalAdditionArrowCell, emptyCell.cloneNode());
  additionRow.append(emptyCell.cloneNode(), totalAdditionCell, emptyCell.cloneNode());

  table.append(clockRow, intermediateRow, arrowRow, timeRow, additionArrowRow, additionRow);

  return table.outerHTML;
}
